import { defineComponent as _defineComponent } from 'vue'
import { Ref, computed, onMounted, onUnmounted, ref, useSlots } from 'vue';
import CButton from '@/components/shared/CButton';
import CError from '@/components/shared/CError.vue';
import CPreview from '@/components/shared/CPreview.vue';
import { detectFileType, isValidFileSignature } from '@/components/shared/CFileInput/utils/fileSignatureValidation';
import { useToaster } from '@/composables/toaster';
import { TYPE } from 'vue-toastification';

/**
 * Internal flag. When true, a rejected file reports its real detected format
 * (e.g. "This file looks like a PSD file…") instead of the generic message.
 * Flip to false to fall back to the original generic copy.
 */
type InvalidFile = { name: string; detectedType: string | null };


export default /*#__PURE__*/_defineComponent({
  __name: 'CFileInput',
  props: {
    value: null,
    initialFilesSrc: { default: () => [] },
    allowSrcRemove: { type: Boolean },
    dragAndDrop: { type: Boolean },
    multiple: { type: Boolean },
    accept: { default: '*' },
    maxMbSize: { default: 10 },
    buttonText: null,
    previewButtonText: null,
    displayPreview: { type: Boolean, default: true },
    previewLabel: null,
    disabled: { type: Boolean },
    error: { type: Boolean },
    errorMsg: null
  },
  emits: ["input", "cancel", "src-preview-removed"],
  setup(__props: any, { expose, emit }: { emit: ({
    (event: 'input', value: File[]): void;
    (event: 'cancel'): void;
    (event: 'src-preview-removed', value: string[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: File[], initialFilesSrc: string[], allowSrcRemove?: boolean, dragAndDrop?: boolean, multiple?: boolean, accept: string, maxMbSize: number, buttonText?: string, previewButtonText?: string, displayPreview: boolean, previewLabel?: string, disabled?: boolean, error?: boolean, errorMsg?: string };

const ENRICH_INVALID_SIGNATURE_MESSAGE = true;

/** Max invalid files listed individually before collapsing the rest into "…and N more". */
const MAX_LISTED_INVALID_FILES = 10;





const slots = useSlots();
const toaster = useToaster();

const fileInputRef: Ref<HTMLInputElement | null> = ref(null);

const _initialSrcs = ref<string[]>(props.initialFilesSrc);

const files = computed({
    get: () => (props.value.length ? props.value : [..._initialSrcs.value]),
    set: (files: string[] | File[]): void => emit('input', files as File[])
});

const previewMode = computed(() => files.value.length);

const allowPreviewRemove = computed(
    () => !props.disabled && ((files.value.length !== 1 && !props.dragAndDrop) || props.allowSrcRemove)
);

const btnText = computed(() => (previewMode.value ? props.previewButtonText : props.buttonText));

const openFileUploader = () => fileInputRef.value?.click();

const handleFileChange = async (event: Event) => {
    const element = event.target as HTMLInputElement;
    await updateModel(element.files);
};

const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    if (!event.dataTransfer || props.disabled) return;
    event.dataTransfer.dropEffect = 'copy';
};

const handleDrop = async (event: DragEvent) => {
    event.preventDefault();
    if (!event.dataTransfer || props.disabled) return;

    await updateModel(event.dataTransfer.files, props.multiple);
};

const removeFile = (index?: number) => {
    clearInput();

    if (index == undefined) {
        files.value = [];
        return;
    }

    if (typeof files.value[index] == 'string') {
        _initialSrcs.value.splice(index, 1);
        emit('src-preview-removed', _initialSrcs.value);
        return;
    }

    files.value.splice(index, 1);
};

const clearInput = () => {
    if (!fileInputRef.value) return;
    fileInputRef.value.value = '';
};

const EXTENSION_ALIASES: Record<string, string> = { jpg: 'jpeg', tif: 'tiff' };
const normalizeExt = (ext: string): string => EXTENSION_ALIASES[ext] ?? ext;

const getFileExtension = (name: string): string => {
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
};

// The toast content is rendered via v-html, so escape anything derived from the
// file (name/extension) to avoid breaking the markup with special characters.
const escapeHtml = (value: string): string =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Reason a single file was rejected, distinguishing two very different cases:
 *  - the file IS the format its extension claims but that format isn't accepted
 *    here (the user already knows the type — don't say "looks like a X");
 *  - the file is mislabeled: its real content is a different format than the
 *    extension declares (e.g. a PSD renamed to .jpg).
 */
const invalidFileReason = ({ name, detectedType }: InvalidFile): string => {
    if (!detectedType) return 'unrecognized format';

    const declaredExt = getFileExtension(name);
    if (declaredExt && normalizeExt(detectedType) === normalizeExt(declaredExt)) {
        return `${detectedType.toUpperCase()} not allowed here`;
    }
    // Signature gives us certainty, so state it: "it's a X", not "it looks like a X".
    return `it's a ${detectedType.toUpperCase()}, not a .${escapeHtml(declaredExt) || '?'}`;
};

const buildInvalidSignatureMessage = (invalidFiles: InvalidFile[]): string => {
    if (!ENRICH_INVALID_SIGNATURE_MESSAGE) {
        return invalidFiles.length === 1
            ? 'This file is not valid. Please try another one or contact support.'
            : 'Some files are not valid. Please try others or contact support.';
    }

    const accepted = escapeHtml(props.accept && props.accept !== '*' ? props.accept : 'the accepted formats');

    if (invalidFiles.length === 1) {
        const reason = invalidFileReason(invalidFiles[0]);
        return `${reason.charAt(0).toUpperCase()}${reason.slice(1)}. Allowed: ${accepted}.`;
    }

    // One file per line (rendered as HTML). Cap the list so a large batch doesn't
    // become an unreadable wall of text: show the first few by name+reason, then
    // summarize the rest as "…and N more (FORMATS)".
    const shown = invalidFiles.slice(0, MAX_LISTED_INVALID_FILES);
    const hidden = invalidFiles.slice(MAX_LISTED_INVALID_FILES);
    const lines = shown.map((file) => `&bull; <b>${escapeHtml(file.name)}</b> — ${invalidFileReason(file)}`);

    if (hidden.length) {
        const formats = [
            ...new Set(hidden.map((file) => (file.detectedType ? file.detectedType.toUpperCase() : 'unknown')))
        ];
        lines.push(`&bull; …and ${hidden.length} more (${formats.join(', ')})`);
    }

    return `${invalidFiles.length} files skipped:<br>${lines.join('<br>')}<br>Allowed: ${accepted}.`;
};

// Error toasts default to 10s; grow the window for multi-file lists so they can be
// read, based on how many lines are actually shown (capped by the truncation).
const invalidSignatureToastTimeout = (invalidCount: number): number => {
    if (invalidCount <= 1) return 10000;
    const shownLines = Math.min(invalidCount, MAX_LISTED_INVALID_FILES + 1);
    return Math.min(30000, 10000 + shownLines * 3000);
};

const updateModel = async (list: FileList | null, concat?: boolean) => {
    const newFiles: File[] = [];
    const invalidFiles: InvalidFile[] = [];

    /**
     * Use a forof to manually push each file to model array,
     * since Array.from and spread operator dont work properly
     * clonning a FileList
     */
    for (const file of list || []) {
        if (!(await isValidFileSignature(file, props.accept))) {
            invalidFiles.push({
                name: file.name,
                detectedType: ENRICH_INVALID_SIGNATURE_MESSAGE ? await detectFileType(file) : null
            });
            continue;
        }
        if (!props.multiple && newFiles.length) break;
        newFiles.push(file);
    }
    if (invalidFiles.length) {
        toaster.show(buildInvalidSignatureMessage(invalidFiles), {
            type: TYPE.ERROR,
            timeout: invalidSignatureToastTimeout(invalidFiles.length)
        });
    }

    // Nothing survived the signature check: leave the current selection untouched and
    // don't emit. Consumers act on `input` immediately (opening a background, uploading
    // a font...), so emitting an empty list would make them continue with `files[0]`
    // undefined. The toast already told the user why, and they can pick another file.
    // Reset the native input too, so re-picking the *same* filename (after fixing the
    // file on disk) still fires `change` instead of looking frozen.
    if (!newFiles.length && invalidFiles.length) {
        clearInput();
        return;
    }

    files.value = concat ? [...(files.value as File[]), ...newFiles] : newFiles;
};

const selectionCanceled = () => {
    clearInput();
    emit('cancel');
};

onMounted(() => {
    fileInputRef.value?.addEventListener('cancel', selectionCanceled);
});

onUnmounted(() => {
    fileInputRef.value?.removeEventListener('cancel', selectionCanceled);
});

expose({ openFileUploader });

return { __sfc: true,ENRICH_INVALID_SIGNATURE_MESSAGE, MAX_LISTED_INVALID_FILES, props, emit, slots, toaster, fileInputRef, _initialSrcs, files, previewMode, allowPreviewRemove, btnText, openFileUploader, handleFileChange, handleDragOver, handleDrop, removeFile, clearInput, EXTENSION_ALIASES, normalizeExt, getFileExtension, escapeHtml, invalidFileReason, buildInvalidSignatureMessage, invalidSignatureToastTimeout, updateModel, selectionCanceled, CButton, CError, CPreview }
}

})